import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarBlank, MagnifyingGlass, ArrowRight, Square } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { formatDuration, shortDate, tickDate } from "@/lib/analyticsFormat";

/**
 * Daily-breakdown section embedded inside AdminAnalytics.
 * - Top toolbar lets admin pick a specific date → table shows watch time per employee for that day
 * - Heatmap below shows the full matrix (employees × last N days, color-graded)
 *
 * Backend: GET /api/analytics/heatmap?days=30
 */
export default function AnalyticsHeatmap({ days = 30 }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [pickedDate, setPickedDate] = useState(""); // YYYY-MM-DD; "" means "all days"

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/analytics/heatmap?days=${days}`);
        if (!alive) return;
        setData(data);
        if (data?.dates?.length) {
          // Default to the most recent day so the table shows something useful
          setPickedDate(data.dates[data.dates.length - 1]);
        }
      } catch (e) {
        if (alive) setError(formatApiError(e, "Could not load daily breakdown."));
      }
    })();
    return () => {
      alive = false;
    };
  }, [days]);

  // Max seconds in any single cell — drives heatmap color scale
  const maxSecondsCell = useMemo(() => {
    if (!data) return 0;
    let m = 0;
    for (const e of data.employees) {
      for (const s of e.daily) {
        if (s > m) m = s;
      }
    }
    return m;
  }, [data]);

  // Filter employees by search query
  const filteredEmployees = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.employees;
    return data.employees.filter(
      (e) =>
        (e.name || "").toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  // For the date-picker table: column index of the picked date
  const pickedDateIdx = useMemo(() => {
    if (!data || !pickedDate) return -1;
    return data.dates.indexOf(pickedDate);
  }, [data, pickedDate]);

  // For the picked-date table: employees who had any watch time on that day, sorted desc
  const pickedDateRows = useMemo(() => {
    if (!data || pickedDateIdx < 0) return [];
    return filteredEmployees
      .map((e) => ({
        user_id: e.user_id,
        name: e.name,
        email: e.email,
        seconds: e.daily[pickedDateIdx] || 0,
      }))
      .sort((a, b) => b.seconds - a.seconds);
  }, [filteredEmployees, pickedDateIdx, data]);

  const dailyTotal = pickedDateRows.reduce((s, r) => s + r.seconds, 0);
  const dailyActive = pickedDateRows.filter((r) => r.seconds > 0).length;

  // Heatmap cell color — orange shade proportional to seconds (0 -> ~white, max -> #E25822)
  const cellStyle = (seconds) => {
    if (!maxSecondsCell || !seconds) return { background: "#FAF7F2" };
    const t = Math.min(1, seconds / maxSecondsCell);
    // Interpolate from #FAF7F2 to #E25822 in HSL-ish way (simple alpha blend on orange)
    const alpha = 0.15 + 0.85 * t;
    return { background: `rgba(226, 88, 34, ${alpha.toFixed(2)})` };
  };

  if (error) {
    return (
      <div className="bg-white border border-[#E5E2DC] rounded-md p-5 text-sm text-[#993333]">
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-white border border-[#E5E2DC] rounded-md p-10 text-center text-sm text-[#8A8A85]">
        Loading daily breakdown…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-heatmap-section">
      {/* Date-picker view */}
      <div className="bg-white border border-[#E5E2DC] rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E5E2DC] flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-heading text-lg font-semibold text-[#1C1C19]">Daily breakdown</div>
            <div className="text-xs text-[#5C5C58]">
              Pick a date to see how much each employee watched on that day
            </div>
          </div>
          <div className="flex items-center gap-2">
            <CalendarBlank size={16} className="text-[#5C5C58]" />
            <select
              data-testid="heatmap-date-picker"
              value={pickedDate}
              onChange={(e) => setPickedDate(e.target.value)}
              className="px-3 py-2 text-sm border border-[#E5E2DC] rounded-md focus:outline-none focus:border-[#E25822] bg-white"
            >
              {[...data.dates].reverse().map((d) => (
                <option key={d} value={d}>
                  {shortDate(d)}
                </option>
              ))}
            </select>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A85]" />
              <input
                type="text"
                data-testid="heatmap-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter employees…"
                className="pl-8 pr-3 py-2 text-sm border border-[#E5E2DC] rounded-md w-56 focus:outline-none focus:border-[#E25822]"
              />
            </div>
          </div>
        </div>

        {/* Day-summary strip */}
        <div className="px-5 py-3 bg-[#FAF7F2] border-b border-[#E5E2DC] flex items-center gap-6 text-xs">
          <div>
            <span className="text-[#5C5C58] uppercase tracking-wider font-semibold">Date</span>{" "}
            <span className="text-[#1C1C19] font-semibold">{shortDate(pickedDate)}</span>
          </div>
          <div>
            <span className="text-[#5C5C58] uppercase tracking-wider font-semibold">Total team watch</span>{" "}
            <span className="text-[#1C1C19] font-semibold">{formatDuration(dailyTotal)}</span>
          </div>
          <div>
            <span className="text-[#5C5C58] uppercase tracking-wider font-semibold">Active employees</span>{" "}
            <span className="text-[#1C1C19] font-semibold">{dailyActive} / {filteredEmployees.length}</span>
          </div>
        </div>

        {/* Picked-date table */}
        <div className="max-h-80 overflow-y-auto">
          {pickedDateRows.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-[#8A8A85]">No employees match this filter.</div>
          ) : (
            <table className="w-full text-sm" data-testid="heatmap-day-table">
              <thead className="bg-white text-xs uppercase tracking-wider text-[#5C5C58] sticky top-0">
                <tr className="border-b border-[#EFECE5]">
                  <th className="px-5 py-2 text-left font-semibold">Employee</th>
                  <th className="px-5 py-2 text-right font-semibold">Watched</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {pickedDateRows.map((r) => (
                  <tr key={r.user_id} className="border-t border-[#EFECE5] hover:bg-[#FAF7F2]">
                    <td className="px-5 py-2.5">
                      <div className="font-semibold text-[#1C1C19]">{r.name || "—"}</div>
                      <div className="text-xs text-[#8A8A85]">{r.email}</div>
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      <span
                        className={
                          r.seconds > 0
                            ? "text-[#1C1C19] font-medium"
                            : "text-[#A8A8A1]"
                        }
                      >
                        {r.seconds > 0 ? formatDuration(r.seconds) : "—"}
                      </span>
                    </td>
                    <td className="px-3">
                      <Link
                        to={`/admin/analytics/employee/${r.user_id}`}
                        className="text-[#E25822] hover:text-[#C44E1A] inline-flex"
                      >
                        <ArrowRight size={14} weight="bold" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Heatmap matrix */}
      <div className="bg-white border border-[#E5E2DC] rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E5E2DC] flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-heading text-lg font-semibold text-[#1C1C19]">Activity heatmap</div>
            <div className="text-xs text-[#5C5C58]">
              Last {data.days} days · darker = more time watched. Hover any cell for exact minutes.
            </div>
          </div>
          {/* Legend */}
          <div className="flex items-center gap-1.5 text-[10px] text-[#5C5C58] uppercase tracking-wider font-semibold">
            <span>Less</span>
            {[0.05, 0.25, 0.5, 0.75, 1].map((t) => (
              <span
                key={t}
                className="block w-4 h-4 rounded-sm"
                style={{ background: `rgba(226, 88, 34, ${(0.15 + 0.85 * t).toFixed(2)})` }}
              />
            ))}
            <span>More</span>
          </div>
        </div>

        <div className="overflow-x-auto max-h-[480px]">
          {filteredEmployees.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-[#8A8A85]">
              No employees match this filter.
            </div>
          ) : (
            <table className="text-xs" data-testid="heatmap-matrix-table">
              <thead className="bg-white sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-[#5C5C58] sticky left-0 bg-white border-b border-r border-[#EFECE5] z-20 min-w-[180px]">
                    Employee
                  </th>
                  {data.dates.map((d) => (
                    <th
                      key={d}
                      className="px-1 py-2 text-center font-semibold text-[10px] text-[#5C5C58] border-b border-[#EFECE5] whitespace-nowrap"
                      title={shortDate(d)}
                    >
                      {tickDate(d)}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right font-semibold text-[#5C5C58] border-b border-l border-[#EFECE5] sticky right-0 bg-white">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((e) => (
                  <tr key={e.user_id} className="hover:bg-[#FAF7F2]/40" data-testid={`heatmap-row-${e.user_id}`}>
                    <td className="px-3 py-1.5 sticky left-0 bg-white hover:bg-[#FAF7F2]/40 border-r border-[#EFECE5] z-10">
                      <Link
                        to={`/admin/analytics/employee/${e.user_id}`}
                        className="text-[#1C1C19] hover:text-[#E25822] font-medium block truncate max-w-[180px]"
                        title={e.email}
                      >
                        {e.name || e.email.split("@")[0]}
                      </Link>
                    </td>
                    {e.daily.map((s, i) => (
                      <td
                        key={data.dates[i]}
                        style={cellStyle(s)}
                        className="w-7 h-7 text-center cursor-default"
                        title={`${shortDate(data.dates[i])} · ${formatDuration(s)}`}
                      />
                    ))}
                    <td className="px-3 py-1.5 text-right text-[#1C1C19] font-semibold border-l border-[#EFECE5] sticky right-0 bg-white">
                      {formatDuration(e.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
